import React, { useEffect, useRef, useState } from "react";
import { GameUIContext, useGameController, useGameDirective } from "@/components/game/GameUIContext.js";
import { PlayerStateUI } from "@/components/game/playerstate/PlayerStateUI.js";
import BoardUI from "@/components/game/board/BoardUI.js";
import { GameMenu } from "@/components/game/menu/GameMenu.js";
import { EndGameState } from "@rosette/ts/src/royalur/engine/state/EndGameState.js";
import { useGameTheme } from "@/components/game/theme/GameThemeContext.js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useGameAPI } from "@/components/api/GameAPIContext.js";
import { invalidateRecentGames } from "@/app/(api)/api/listGames/useRecentGames.js";
import { useRune } from "@rosette/react_utils/src/useRune.js";
import type { GameSource } from "@rosette/ts/src/business/game/controller/source/GameSource.js";
import type { GameClientControls } from "@rosette/ts/src/business/GameClientControls.js";
import { useAssetLoader } from "@/components/assets/AssetContext.js";
import { useGamePreferences } from "@/app/(api)/api/getGamePreferences/useGamePreferences.js";
import { useOptionalUser } from "@/components/env/EnvContext.js";
import { AudioSettings } from "@rosette/web_components/src/assets/AudioSettings.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { OnlineGameSource } from "@rosette/ts/src/business/game/controller/source/OnlineGameSource.js";
import { useCreateSharedRendererState } from "@rosette/web_components/src/game/SharedRendererState.js";
import { useGameLayout } from "@rosette/web_components/src/game/useGameLayout.js";
import { requestNotificationPermission } from "@rosette/web_utils/src/game/notifications.js";
import { Role } from "@rosette/ts/src/business/role/Role.js";
import { Permission } from "@rosette/ts/src/business/role/Permission.js";
import { LANDSCAPE, useDeviceInfo } from "@rosette/web_components/src/accessibility/DeviceInfoContext.js";
import { TallAdBanner } from "@rosette/web_components/src/ads/TallAdBanner.js";
import Link from "next/link";
import { HalfPageAdBanner } from "@rosette/web_components/src/ads/HalfPageAdBanner.js";
import { useGameAchievementSystem } from "@/utils/achievements/useGameAchievementSystem.js";
import { useToastActions } from "@rosette/web_components/feedback/toast/ToastContext.js";
import { addEarnedAchievementToasts } from "@/utils/achievements/addEarnedAchievementToasts.js";
import { useAchievementResources } from "@/components/achievements/useAchievementResources.js";
import { GameArea, GameBottom, GameRow, MaybeVisibleDiceUI } from "@/components/game/GameLayout.js";


function InternalGameUI() {
    const queryClient = useQueryClient();
    const theme = useGameTheme();
    const assetLoader = useAssetLoader();
    const controller = useGameController();
    const directive = useGameDirective();
    const gameAPI = useGameAPI();
    const lobbyStatus = useRune(controller.getLobbyStatus());
    const error = useRune(controller.getError());
    const user = useOptionalUser();
    const { preferences } = useGamePreferences(user);
    const { addToast } = useToastActions();

    const containerRef = useRef<HTMLDivElement | null>(null);
    const gameLayout = useGameLayout(containerRef);
    const isMobile = (gameLayout !== "landscape");

    const players = useRune(controller.getPlayers());
    const yourPlayer = players.getYourPlayer();
    const leftPlayer = players.getLeftPlayer();
    const rightPlayer = players.getRightPlayer();

    const sharedRendererState = useCreateSharedRendererState();
    const achievementResources = useAchievementResources();

    const gameState = directive.state;
    const winGameState = (gameState instanceof EndGameState ? gameState : null);
    const isGameWon = (winGameState !== null);

    const [isPathActiveRaw, setPathActive] = useState(false);

    let isPathActive = isPathActiveRaw;
    if (!lobbyStatus.isGameAvailable()) {
        isPathActive = false;
    }

    // Upload the game when the game is finished.
    const uploadGame = useMutation({
        mutationFn: async () => {
            try {
                const source = controller.getGameSource();
                if (!source.shouldUploadGame())
                    return [];

                const earnedAchievements = await gameAPI.uploadGame(source);

                const finishedGameID = source.lobbyStatus.get().getCompleteGameID();
                if (finishedGameID) {
                    invalidateRecentGames(queryClient);
                }

                return earnedAchievements;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        onSuccess: (earnedAchievements) => {
            addEarnedAchievementToasts(earnedAchievements, addToast, achievementResources);
        },
    });
    useEffect(() => controller.addWinListener(() => {
        if (controller.getGameSource().shouldUploadGame()) {
            uploadGame.mutate();
        }
    }), [controller, uploadGame]);

    // Listen for earned achievements from online games.
    useEffect(() => controller.getGameSource().addEarnedAchievementsListener((earnedAchievements) => {
        addEarnedAchievementToasts(earnedAchievements, addToast, achievementResources);
    }), [controller, addToast, achievementResources]);

    const { registerGameAchievementHooks } = useGameAchievementSystem(controller);
    useEffect(registerGameAchievementHooks, [registerGameAchievementHooks]);

    // Apply user preferences.
    useEffect(() => {
        const audioSettings = assetLoader.getAudioSettings();
        audioSettings.set(new AudioSettings(
            preferences.soundVolume / 100.0,
            preferences.musicVolume / 100.0,
            audioSettings.get().musicDisabled,
        ));
    }, [assetLoader, preferences]);

    // We don't want the user to accidentally reload and lose their game.
    useEffect(() => {
        // Online game sources can safely be reloaded.
        if (controller.getGameSource() instanceof OnlineGameSource)
            return;

        window.onbeforeunload = () => true;
        return () => {
            window.onbeforeunload = null;
        };
    }, [controller]);

    // We want to request notifications preemptively.
    const isGameAvailable = lobbyStatus.isGameAvailable();
    const gameMode = lobbyStatus.getGameMode();
    const isGameModeRemote = (gameMode !== null && gameMode.isRemote());
    useEffect(() => {
        if (!isGameAvailable && isGameModeRemote) {
            void requestNotificationPermission();
        }
    }, [isGameAvailable, isGameModeRemote]);

    const didYourPlayerWin = (
        winGameState !== null && yourPlayer === winGameState.getWinner()
    );

    return (
        <GameArea
            containerRef={containerRef}
            backgroundTileSrc={theme.getThemeSettings().backgroundTile.src}
            className="grow">

            <GameMenu
                isMobile={isMobile}
                isGameWon={isGameWon}
                didYourPlayerWin={didYourPlayerWin}
                error={error}
                uploadGame={uploadGame}
                isPathActive={isPathActive}
                setPathActive={setPathActive} />

            <GameRow
                gameLayout={gameLayout}
                left={(
                    <PlayerStateUI
                        playerType={leftPlayer}
                        gameLayout={gameLayout}
                        positionLeft={true}
                        hideReactionUI={false}
                        sharedRendererState={sharedRendererState}>

                        {gameLayout !== "portrait" && (
                            <MaybeVisibleDiceUI
                                gameLayout={gameLayout}
                                visible={leftPlayer === directive.activePlayer}
                                playerFilter={leftPlayer} />
                        )}
                    </PlayerStateUI>
                )}
                board={(
                    <BoardUI
                        isMobile={isMobile}
                        isPathActive={isPathActive}
                        sharedRendererState={sharedRendererState} />
                )}
                right={(
                    <PlayerStateUI
                        playerType={rightPlayer}
                        gameLayout={gameLayout}
                        positionLeft={false}
                        hideReactionUI={false}
                        sharedRendererState={sharedRendererState}>

                        {gameLayout !== "portrait" && (
                            <MaybeVisibleDiceUI
                                gameLayout={gameLayout}
                                visible={rightPlayer === directive.activePlayer}
                                playerFilter={rightPlayer} />
                        )}
                    </PlayerStateUI>
                )} />

            {gameLayout === "portrait" && (
                <GameBottom>
                    <MaybeVisibleDiceUI
                        gameLayout={gameLayout}
                        visible={true}
                        playerFilter={null} />
                </GameBottom>
            )}
        </GameArea>
    );
}


export interface GameProps {
    gameSource: GameSource;
    clientControls: GameClientControls;
}

export default function GameUI({ gameSource, clientControls }: GameProps) {
    const user = useOptionalUser();
    const roles = Role.list(user);
    const adsEnabled = !Permission.NO_ADS.hasPermission(roles);

    const deviceInfo = useDeviceInfo();
    const useHalfPage = (deviceInfo.width > 1200);

    const becomeAMemburToRemoveAdsElem = (
        <p className="text-base py-4 text-center leading-none">
            <Link href="/premium">
                Hide ads forever with Gold
            </Link>
        </p>
    );

    return (
        <GameUIContext gameSource={gameSource} clientControls={clientControls}>
            <div className={cn(
                "flex relative w-full h-full max-h-full max-w-full",
                deviceInfo.orientation === LANDSCAPE ? "flex-row" : "flex-col",
            )}>
                <InternalGameUI />

                {adsEnabled && !deviceInfo.isDefault && deviceInfo.orientation === LANDSCAPE && (
                    <div className={cn(
                        "min-h-[30px] shrink-0 overflow-hidden",
                        useHalfPage ? "min-w-[300px]" : "min-w-[160px]",
                    )}>
                        {useHalfPage && (
                            <HalfPageAdBanner>
                                {becomeAMemburToRemoveAdsElem}
                            </HalfPageAdBanner>
                        )}

                        {!useHalfPage && (
                            <TallAdBanner>
                                {becomeAMemburToRemoveAdsElem}
                            </TallAdBanner>
                        )}
                    </div>
                )}
            </div>
        </GameUIContext>
    );
}
